import React, { useState, useEffect, useMemo } from 'react';
import {
  DocumentTextIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { OCRApiService } from '../services/api';
import { InvoiceData, LineItem } from '../types/invoice';
import VendorLookup from './VendorLookup';
import VendorAddressSelect from './VendorAddressSelect';
import POLookup from './POLookup';
import GLPicker from './GLPicker';
import POLineMatcher from './POLineMatcher';
import { useAuth } from '../contexts/AuthContext';
import { isFeatureEnabled } from '../config/accountSettings';

/**
 * Apply the tenant's GL ID format as an input mask.
 *
 * The template comes from the tenant's `gl_format_example` account setting
 * (e.g. Cleveland: "010-441-5-250-00"). Digits in the template become
 * fillable slots; anything else (hyphens, dots) is inserted automatically as
 * the clerk types. So typing "0104415250" yields "010-441-5-250".
 *
 *   - Non-digits in the raw input are stripped, then re-applied per template
 *   - If the clerk pastes an already-formatted value, we normalize it
 *   - If more digits are typed than the template has slots, the excess is
 *     truncated
 *   - If no template is configured, the raw input is passed through unchanged
 */
export function applyGlMask(raw: string, template: string | undefined | null): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!template) return String(raw ?? '');
  const maxDigits = (template.match(/\d/g) || []).length;
  if (maxDigits === 0) return String(raw ?? '');
  const capped = digits.slice(0, maxDigits);
  let out = '';
  let d = 0;
  for (let t = 0; t < template.length && d < capped.length; t++) {
    const ch = template[t];
    if (/\d/.test(ch)) {
      out += capped[d];
      d++;
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * Suffix a value with the untyped tail of the template so the clerk always
 * sees the full mask shape (like a phone-number input). Digit slots in the
 * tail become `_`; separators stay literal.
 *
 * value=`010-4`, template=`010-441-5-250-00` → tail=`__-_-___-__`
 * value=``,       template=`010-441-5-250-00` → tail=`___-___-_-___-__`
 */
export function glMaskTail(value: string, template: string | undefined | null): string {
  if (!template) return '';
  const consumed = String(value ?? '').length;
  if (consumed >= template.length) return '';
  return template
    .slice(consumed)
    .split('')
    .map((ch) => (/\d/.test(ch) ? '_' : ch))
    .join('');
}

/**
 * Zero-fill an in-progress GL to the tenant's full template length.
 * Used on blur so a partial entry (`010-441-5`) becomes valid (`010-441-5-000-00`).
 * No-op if the value is empty or the template is not configured.
 */
export function padGlToTemplate(value: string, template: string | undefined | null): string {
  if (!value || !template) return value;
  const digitsSoFar = String(value).replace(/\D/g, '');
  const templateDigitCount = (template.match(/\d/g) || []).length;
  if (digitsSoFar.length === 0 || digitsSoFar.length >= templateDigitCount) return value;
  const padded = digitsSoFar + '0'.repeat(templateDigitCount - digitsSoFar.length);
  return applyGlMask(padded, template);
}

// Define stable components outside the main component to prevent recreation
const FormRow: React.FC<{
  label: string;
  children: React.ReactNode;
  required?: boolean;
  tooltip?: string;
}> = ({ label, children, required = false, tooltip }) => (
  <div className="grid grid-cols-12 gap-4 py-2 border-b border-gray-100 dark:border-gray-700">
    <div className="col-span-4 flex items-center gap-1">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {tooltip && (
        <InformationCircleIcon
          className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help"
          title={tooltip}
          aria-label={tooltip}
        />
      )}
    </div>
    <div className="col-span-8">
      {children}
    </div>
  </div>
);

const TextInput: React.FC<{
  value: string | number | undefined;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  className?: string;
  isOCRExtracted?: boolean;
}> = ({ value, onChange, type = 'text', placeholder, className = '' }) => {
  return (
    <div className="relative">
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
          focus:ring-2 focus:ring-brand-500 focus:border-brand-500
          dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 relative z-30 ${className}`}
        autoComplete="off"
      />
    </div>
  );
};

const TextArea: React.FC<{
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  isOCRExtracted?: boolean;
}> = ({ value, onChange, placeholder, rows = 2 }) => {
  return (
    <div className="relative">
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
          focus:ring-2 focus:ring-brand-500 focus:border-brand-500
          dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 relative z-30"
        autoComplete="off"
      />
    </div>
  );
};

interface InvoiceEditFormProps {
  data: InvoiceData;
  filename?: string;
  onSave?: (updatedData: InvoiceData) => Promise<void>;
  onCancel?: () => void;
  onChange?: (updatedData: InvoiceData) => void;
  dirtyRef?: React.MutableRefObject<boolean>;  // parent can read dirty state without re-renders
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;  // parent can trigger save
  className?: string;
}

// Utility function to check if a field was extracted from OCR (has meaningful content)
const isFieldExtracted = (value: any, originalData: InvoiceData): boolean => {
  // If no value, it's not extracted
  if (!value) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (typeof value === 'number' && value === 0) return false;
  
  // For string values, check if they appear to be meaningful (not just defaults)
  if (typeof value === 'string') {
    const stringValue = value.trim();
    
    // Common default/empty patterns that shouldn't be considered extracted
    const defaultPatterns = ['', 'N/A', 'n/a', 'TBD', 'tbd', 'Unknown', 'unknown'];
    if (defaultPatterns.includes(stringValue)) return false;
    
    // If we have raw text, check if the value appears in it
    const rawText = originalData.raw_text?.toLowerCase() || '';
    if (rawText && stringValue.length > 2) {
      // For dates, check for partial matches (since format might be different)
      if (stringValue.match(/\d{4}/) || stringValue.match(/\d{1,2}\/\d{1,2}/)) {
        return true; // Assume dates with numbers are extracted
      }
      
      // Check if the value (or parts of it) exist in raw text
      const lowerValue = stringValue.toLowerCase();
      return rawText.includes(lowerValue) || 
             lowerValue.split(/\s+/).some(word => word.length > 2 && rawText.includes(word));
    }
    
    // If no raw text to compare against, assume it's extracted if it has meaningful content
    return stringValue.length > 0;
  }
  
  // For numbers, assume they're extracted if they're not zero
  if (typeof value === 'number') {
    return value !== 0;
  }
  
  return true;
};

// Utility function to convert various date formats to YYYY-MM-DD
const convertToDateInputFormat = (dateString: string | undefined): string => {
  if (!dateString) return '';
  
  try {
    // Handle common date formats
    let cleanDate = dateString.trim();
    
    // Handle "Mar 18, 2556" or "September 7, 2025" format (note: 2556 might be a Buddhist calendar year)
    const monthYearMatch = cleanDate.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
    if (monthYearMatch) {
      const [, month, day, year] = monthYearMatch;
      const monthMap: { [key: string]: string } = {
        'Jan': '01', 'January': '01',
        'Feb': '02', 'February': '02',
        'Mar': '03', 'March': '03',
        'Apr': '04', 'April': '04',
        'May': '05',
        'Jun': '06', 'June': '06',
        'Jul': '07', 'July': '07',
        'Aug': '08', 'August': '08',
        'Sep': '09', 'September': '09',
        'Oct': '10', 'October': '10',
        'Nov': '11', 'November': '11',
        'Dec': '12', 'December': '12'
      };
      
      if (monthMap[month]) {
        // Convert Buddhist calendar year to Gregorian if needed (2556 -> 2013)
        let gregorianYear = parseInt(year);
        if (gregorianYear > 2500) {
          gregorianYear = gregorianYear - 543; // Buddhist to Gregorian conversion
        }
        
        const paddedDay = day.padStart(2, '0');
        return `${gregorianYear}-${monthMap[month]}-${paddedDay}`;
      }
    }
    
    // Handle MM/DD/YYYY format
    const slashMatch = cleanDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, month, day, year] = slashMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Handle DD-MM-YYYY format
    const dashMatch = cleanDate.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (dashMatch) {
      const [, day, month, year] = dashMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Handle YYYY-MM-DD format (already correct, just return it)
    const isoMatch = cleanDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      return cleanDate;
    }
    
    // Try to parse with Date constructor as fallback
    // IMPORTANT: Append T12:00:00 to avoid UTC midnight interpretation
    // which can shift the date back by one day in negative UTC offsets
    const parsedDate = new Date(cleanDate + (cleanDate.includes('T') ? '' : 'T12:00:00'));
    if (!isNaN(parsedDate.getTime())) {
      const y = parsedDate.getFullYear();
      const m = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const d = String(parsedDate.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    
    // Last resort: try plain Date constructor
    const fallbackDate = new Date(cleanDate);
    if (!isNaN(fallbackDate.getTime())) {
      const y = fallbackDate.getFullYear();
      const m = String(fallbackDate.getMonth() + 1).padStart(2, '0');
      const d = String(fallbackDate.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    
    // If all else fails, return the original string
    return cleanDate;
  } catch (error) {
    console.warn('Date conversion failed for:', dateString, error);
    return dateString || '';
  }
};

// Utility function to enhance OCR data with defaults
const enhanceInvoiceData = (rawData: InvoiceData): InvoiceData => {
  // Deep copy to prevent any reference issues
  const deepCopyData = JSON.parse(JSON.stringify(rawData));
  
  // Ensure all critical fields have default values if missing
  return {
    // Basic document information with defaults
    company_name: deepCopyData.company_name || '',
    document_type: deepCopyData.document_type || 'invoice',
    confidence_score: deepCopyData.confidence_score || 0,
    
    // Document details with defaults and date format conversion
    invoice_number: deepCopyData.invoice_number || '',
    po_number: deepCopyData.po_number || '',
    invoice_date: convertToDateInputFormat(deepCopyData.invoice_date) || '',
    due_date: convertToDateInputFormat(deepCopyData.due_date) || '',
    invoice_detail_line_items: deepCopyData.invoice_detail_line_items || '',
    
    // Vendor information with proper structure
    vendor: {
      name: deepCopyData.vendor?.name || '',
      vendor_number: deepCopyData.vendor?.vendor_number || '',
      vendor_address_id: deepCopyData.vendor?.vendor_address_id || '',
      post_date: deepCopyData.vendor?.post_date || new Date().toLocaleDateString('en-CA'),
      type_1099: deepCopyData.vendor?.type_1099 || '',
      single_check: deepCopyData.vendor?.single_check || '',
      check_code: deepCopyData.vendor?.check_code || '',
      address: {
        full_address: deepCopyData.vendor?.address?.full_address || '',
        street: deepCopyData.vendor?.address?.street || '',
        city: deepCopyData.vendor?.address?.city || '',
        state: deepCopyData.vendor?.address?.state || '',
        postal_code: deepCopyData.vendor?.address?.postal_code || '',
        country: deepCopyData.vendor?.address?.country || ''
      },
      phone: deepCopyData.vendor?.phone || '',
      email: deepCopyData.vendor?.email || '',
      website: deepCopyData.vendor?.website || '',
      tax_id: deepCopyData.vendor?.tax_id || ''
    },
    
    // Customer information with defaults
    customer: deepCopyData.customer || {
      name: '',
      customer_id: '',
      address: { full_address: '' },
      phone: '',
      email: ''
    },
    
    // Financial information with defaults
    subtotal: deepCopyData.subtotal || 0,
    total_amount: deepCopyData.total_amount || 0,
    currency: deepCopyData.currency || 'USD',
    discount: deepCopyData.discount || 0,
    freight: deepCopyData.freight || 0,
    sub_discount: deepCopyData.sub_discount || 0,
    sub_tax: deepCopyData.sub_tax || 0,
    sub_freight: deepCopyData.sub_freight || 0,
    state_sales_tax_maintenance_code: deepCopyData.state_sales_tax_maintenance_code || '',
    
    // Tax information
    tax_info: deepCopyData.tax_info || [],
    total_tax: deepCopyData.total_tax || 0,
    
      // Line items with validation. Backward compat (per 2026-08-07 decision):
      // handles two OLD-invoice cases so freight/discount show correctly:
      //   1) An existing line item's description says "Shipping cost", "S&H",
      //      "Freight", "Discount", "Promo", etc, but its `category` was never
      //      set because the invoice pre-dates today's extractor. Retag it
      //      based on the description so the badge + tint render.
      //   2) The invoice header has `freight`/`discount` totals but NO matching
      //      line item. Synthesize the line item.
      line_items: (() => {
        const raw: any[] = Array.isArray(deepCopyData.line_items) ? deepCopyData.line_items : [];
        // Description-based classifier. Runs when category is empty.
        const FREIGHT_HINTS = /(shipping|freight|s\s*&\s*h|s\.?\s*&\s*h\.?|handling|delivery|postage|haul\s*trip)/i;
        const DISCOUNT_HINTS = /(discount|promo|promotion|rebate|credit\s*(?:memo|adjustment)|price\s*adjust)/i;
        const classifyFromDescription = (desc: string, existing: string): string => {
          const cur = (existing || '').toLowerCase();
          if (cur === 'freight' || cur === 'discount' || cur === 'regular') return cur;
          const d = (desc || '').trim();
          if (!d) return 'regular';
          if (FREIGHT_HINTS.test(d)) return 'freight';
          if (DISCOUNT_HINTS.test(d)) return 'discount';
          return 'regular';
        };
        const normalized = raw.map((item: any) => ({
          description: item.description || '',
          quantity: typeof item.quantity === 'number' ? item.quantity : 0,
          unit_price: typeof item.unit_price === 'number' ? item.unit_price : 0,
          total_price: typeof item.total_price === 'number' ? item.total_price : (item.quantity || 0) * (item.unit_price || 0),
          category: classifyFromDescription(item.description || '', item.category || ''),
          gl_id: item.gl_id || '',
          matched_po_detail_key: item.matched_po_detail_key || undefined,
          sub_disc: item.sub_disc || '',
          states_sales_tax_maintenance_code: item.states_sales_tax_maintenance_code || '',
          sub_freight: item.sub_freight || '',
          tax_code: item.tax_code || '',
        }));
        const hasFreightLine = normalized.some((li) => (li.category || '').toLowerCase() === 'freight');
        const hasDiscountLine = normalized.some((li) => (li.category || '').toLowerCase() === 'discount');
        const headerFreight = Number(deepCopyData.freight || 0);
        const headerDiscount = Number(deepCopyData.discount || 0);
        if (!hasFreightLine && headerFreight > 0) {
          normalized.push({
            description: 'Freight',
            quantity: 1,
            unit_price: headerFreight,
            total_price: headerFreight,
            category: 'freight',
            gl_id: '',
            matched_po_detail_key: undefined,
            sub_disc: '',
            states_sales_tax_maintenance_code: '',
            sub_freight: '',
            tax_code: '',
          });
        }
        if (!hasDiscountLine && headerDiscount > 0) {
          normalized.push({
            description: 'Discount',
            quantity: 1,
            unit_price: headerDiscount,
            total_price: headerDiscount,
            category: 'discount',
            gl_id: '',
            matched_po_detail_key: undefined,
            sub_disc: '',
            states_sales_tax_maintenance_code: '',
            sub_freight: '',
            tax_code: '',
          });
        }
        return normalized;
      })(),
    
    // Payment information
    payment_info: deepCopyData.payment_info || {
      method: '',
      card_number: '',
      reference: '',
      status: ''
    },
    
    // Additional fields
    notes: deepCopyData.notes || '',
    terms: deepCopyData.terms || '',
    needs_approval: deepCopyData.needs_approval ?? false,

    // Raw data preservation
    raw_text: deepCopyData.raw_text || '',
    structured_data: deepCopyData.structured_data || {}
  };
};

const InvoiceEditForm: React.FC<InvoiceEditFormProps> = ({
  data,
  filename,
  onSave,
  onCancel,
  onChange,
  dirtyRef,
  saveRef,
  className = '',
}) => {
  // Initialize with enhanced data that includes defaults for all required fields
  const [formData, setFormData] = useState<InvoiceData>(() => {
    console.log('Initializing form with data:', data);
    const enhancedData = enhanceInvoiceData(data);
    console.log('Enhanced form data with defaults:', enhancedData);
    return enhancedData;
  });
  
  // Store original data for OCR extraction detection - update when data changes
  const [originalData, setOriginalData] = useState<InvoiceData>(data);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, _setIsDirty] = useState(false);
  const setIsDirty = (v: boolean) => {
    _setIsDirty(v);
    if (dirtyRef) dirtyRef.current = v;
  };
  // PO and GL are no longer mutually exclusive, so no conflict state is
  // tracked here. A line can carry both a PO and a GL simultaneously.
  // Address block is normally locked when a vendor is matched. Clerks can
  // unlock it per-invoice when the vendor has alt addresses that aren't in
  // Keystone's default record (Samantha's #6). This is a per-session override
  // and resets when the invoice is re-opened.
  const [addressManuallyUnlocked, setAddressManuallyUnlocked] = useState(false);
  const [showPOMatcher, setShowPOMatcher] = useState(false);
  const [addItemMenuOpen, setAddItemMenuOpen] = useState(false);

  // Live Keystone data for the matched vendor (1099 type, check code,
  // single check flag defaults). null until a fetch resolves; the fetch
  // is fail-open so a dead Keystone side leaves the fields blank and
  // editable instead of blocking the clerk.
  const [vendorKeystoneData, setVendorKeystoneData] = useState<Record<string, unknown> | null>(null);
  // Tenant check code list from the CHECK.CODES preset. Only fetched when
  // the vendor itself has no default check code and we need a dropdown.
  const [checkCodesList, setCheckCodesList] = useState<Array<{ code: string; desc: string }> | null>(null);
  // Tenant 1099 type list from the 1099-API.PARMS preset. Null until first
  // fetch; empty array when the preset is not deployed yet (fallback to
  // free-text input).
  const [codes1099List, setCodes1099List] = useState<Array<{ code: string; desc: string }> | null>(null);

  // Tenant-specific GL ID format pulled from current user's account.settings.
  // Falls back to lenient "must contain a dash" if the tenant hasn't set one.
  const { user } = useAuth();
  const glFormatSettings = {
    regex: user?.account?.settings?.gl_format_regex as string | undefined,
    placeholder: user?.account?.settings?.gl_format_placeholder as string | undefined,
    label: user?.account?.settings?.gl_format_label as string | undefined,
    example: user?.account?.settings?.gl_format_example as string | undefined,
  };

  // Tax Code field is opt-in per tenant via account.settings.features.state_tax_code.
  const isStateTaxEnabled = isFeatureEnabled(user?.account, 'state_tax_code');

  // Per-tenant tax code list (AIPM-609). Cleveland's account.settings.tax_codes
  // is seeded from the NC State Sales Tax Codes CSV — counties + food taxes
  // + special codes (N=Not Taxable, Y=Default). Other tenants can configure
  // their own list in admin settings. If unset, falls back to a free-text
  // input so the field still works for tenants who haven't configured codes.
  const taxCodeList = (user?.account?.settings as Record<string, unknown> | undefined)?.tax_codes as
    | Array<{ code: string; description?: string }>
    | undefined;

  // Mode-capability flags driven by the KeyNet SSO roles claim (1 / 2 / 3).
  //   can_invoice only       -> lock the editor to Invoice, hide the toggle
  //   can_voucher_req only   -> lock the editor to Voucher Requisition, hide
  //   both                   -> show the toggle (today's behavior)
  // Legacy password / Supabase users default to can_invoice=true, which keeps
  // the historical invoice-only experience.
  const canInvoice = user?.can_invoice ?? true;
  const canVoucherReq = user?.can_voucher_req ?? false;
  const bothModes = canInvoice && canVoucherReq;
  const lockedMode: 'invoice' | 'voucher_req' | null = bothModes
    ? null
    : canVoucherReq
      ? 'voucher_req'
      : canInvoice
        ? 'invoice'
        : null;

  // Only reset form when switching to a different document — not on parent re-renders.
  // We deliberately do NOT depend on `data` to avoid resetting while user is typing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const enhancedData = enhanceInvoiceData(data);
    setFormData(enhancedData);
    setIsDirty(false);
    setOriginalData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user only has one mode available (not both can_invoice AND
  // can_voucher_req), force document_type to that mode on mount — no
  // matter what the OCR extracted or what the saved value is. The toggle
  // is hidden, so without this a saved voucher record viewed by an
  // invoice-only user would still render in voucher mode.
  useEffect(() => {
    if (lockedMode && formData.document_type !== lockedMode) {
      setFormData(prev => ({ ...prev, document_type: lockedMode }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedMode]);

  // Live Keystone VENDOR lookup: whenever the vendor_number changes,
  // pull that vendor's record from Keystone's query.asp to prefill 1099 Type,
  // Check Code, and Single Check with the vendor's defaults. Only fills
  // fields the clerk has not already set so we never clobber a manual edit.
  const vendorNumberForKeystone = formData.vendor?.vendor_number || '';
  useEffect(() => {
    const vno = vendorNumberForKeystone.trim();
    if (!vno) {
      setVendorKeystoneData(null);
      return;
    }
    let cancelled = false;
    OCRApiService.fetchKeystoneVendor(vno).then(res => {
      if (cancelled) return;
      const data = (res && res.data) || null;
      setVendorKeystoneData(data);
      if (!data) return;
      const t1099 = String((data as Record<string, unknown>)['VENDOR.1099.TYPE'] || '').trim();
      const cc = String((data as Record<string, unknown>)['VENDOR.CHECK.CODE'] || '').trim();
      const sc = String((data as Record<string, unknown>)['SINGLE.CHECK.FLAG'] || '').trim();
      setFormData(prev => {
        const vendor = { ...(prev.vendor || {}) };
        let changed = false;
        if (t1099 && !(vendor.type_1099 || '').trim()) { vendor.type_1099 = t1099; changed = true; }
        if (cc && !(vendor.check_code || '').trim()) { vendor.check_code = cc; changed = true; }
        if (sc && !(vendor.single_check || '').trim()) { vendor.single_check = sc; changed = true; }
        return changed ? { ...prev, vendor } : prev;
      });
    });
    return () => { cancelled = true; };
  }, [vendorNumberForKeystone]);

  // Pull the current user's KEYSCAN row once on mount so we can prefill
  // Req Dept from GROUP.ID when the clerk flips the invoice to voucher
  // requisition mode. Blank response is fine, the field stays editable.
  useEffect(() => {
    let cancelled = false;
    OCRApiService.fetchKeystoneMyProfile().then(res => {
      if (cancelled) return;
      const data = (res && res.data) || null;
      if (!data) return;
      const groupId = String((data as Record<string, unknown>)['GROUP.ID'] || '').trim();
      if (!groupId) return;
      setFormData(prev => (prev.req_dept ? prev : { ...prev, req_dept: groupId }));
    });
    return () => { cancelled = true; };
  }, []);

  // Fetch the full CHECK.CODES list on demand, only when the vendor has
  // no default check code (and we need the dropdown). One request per
  // mount at most.
  const vendorHasCheckCode = !!(
    vendorKeystoneData &&
    String((vendorKeystoneData as Record<string, unknown>)['VENDOR.CHECK.CODE'] || '').trim()
  );
  useEffect(() => {
    if (vendorHasCheckCode) return;
    if (checkCodesList !== null) return;
    OCRApiService.fetchKeystoneCheckCodes().then(list => setCheckCodesList(list));
  }, [vendorHasCheckCode, checkCodesList]);

  // One-shot fetch of the 1099 type list. Fail-open to a text input when
  // the preset isn't deployed on the tenant's KeyNet yet.
  useEffect(() => {
    if (codes1099List !== null) return;
    OCRApiService.fetchKeystone1099Types().then(list => setCodes1099List(list));
  }, [codes1099List]);

  // Per 2026-08-07 decision: header financial fields (subtotal, freight,
  // discount) are auto-computed from line items. This effect keeps
  // formData.subtotal / .freight / .discount in sync whenever line items
  // change, so the export code (which reads these header fields) keeps
  // working without any downstream changes. Also recomputes total_amount
  // when the header hasn't been manually set.
  useEffect(() => {
    const items = formData.line_items || [];
    let subtotal = 0;
    let freight = 0;
    let discount = 0;
    for (const it of items) {
      const cat = (it.category || 'regular').toLowerCase();
      const amt =
        it.total_price != null
          ? Number(it.total_price)
          : (Number(it.quantity || 0) * Number(it.unit_price || 0));
      if (isNaN(amt)) continue;
      if (cat === 'freight') freight += amt;
      else if (cat === 'discount') discount += Math.abs(amt);
      else subtotal += amt;
    }
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const newSub = round2(subtotal);
    const newFrt = round2(freight);
    const newDsc = round2(discount);
    if (
      newSub !== round2(formData.subtotal || 0) ||
      newFrt !== round2(formData.freight || 0) ||
      newDsc !== round2(formData.discount || 0)
    ) {
      setFormData((prev) => ({
        ...prev,
        subtotal: newSub,
        freight: newFrt,
        discount: newDsc,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(formData.line_items)]);

  // Weighted distribution. When any regular line has subject_to_freight or
  // subject_to_discount checked, the total freight (sum of freight-tagged
  // line items) or total discount is distributed across the checked lines
  // PROPORTIONAL TO EACH LINE'S TOTAL, with the last-penny remainder on the
  // last subject line. This runs at render time (memoized) so distributions
  // stay in lockstep with edits without a state update cycle.
  //
  // Also tracks whether the standalone freight/discount line items should
  // render as "DISTRIBUTED" (grayed out, not counted in subtotal) because
  // their amount has been split across other lines.
  const distributions = useMemo(() => {
    const items = formData.line_items || [];
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const compute = (categoryKey: 'freight' | 'discount', flagKey: 'subject_to_freight' | 'subject_to_discount') => {
      const totalFromLines = items.reduce((acc, it) => {
        if ((it.category || '').toLowerCase() !== categoryKey) return acc;
        const amt = it.total_price != null
          ? Number(it.total_price)
          : Number(it.quantity || 0) * Number(it.unit_price || 0);
        return acc + (isNaN(amt) ? 0 : Math.abs(amt));
      }, 0);
      const subjectIdxs = items
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => Boolean((it as any)[flagKey]) && (it.category || 'regular').toLowerCase() === 'regular');
      const perLine: Record<number, number> = {};
      if (subjectIdxs.length === 0 || totalFromLines <= 0) {
        return { perLine, totalFromLines: round2(totalFromLines), isDistributed: false };
      }
      const subjectTotal = subjectIdxs.reduce((acc, { it }) => acc + Number(it.total_price || 0), 0);
      if (subjectTotal <= 0) {
        // Fallback: even split when subject lines have no totals yet
        const even = round2(totalFromLines / subjectIdxs.length);
        subjectIdxs.forEach(({ i }) => { perLine[i] = even; });
        return { perLine, totalFromLines: round2(totalFromLines), isDistributed: true };
      }
      let assigned = 0;
      subjectIdxs.forEach(({ it, i }, idx) => {
        if (idx === subjectIdxs.length - 1) {
          // Last line takes the remainder (pennies)
          perLine[i] = round2(totalFromLines - assigned);
        } else {
          const share = round2((Number(it.total_price || 0) / subjectTotal) * totalFromLines);
          perLine[i] = share;
          assigned = round2(assigned + share);
        }
      });
      return { perLine, totalFromLines: round2(totalFromLines), isDistributed: true };
    };
    return {
      freight: compute('freight', 'subject_to_freight'),
      discount: compute('discount', 'subject_to_discount'),
    };
  }, [formData.line_items]);

  // Derived state for mutual exclusion
  const hasPO = Boolean(formData.po_number?.trim());
  const hasAnyGlId = formData.line_items?.some(item => item.gl_id?.trim()) || false;

  // Voucher-requisition mode. Toggled by the doc-type radio at the top of
  // the form. When on: hide PO field + PO matcher, drop qty/unit_price per
  // line (voucher recs enter cost directly per the requisition layout),
  // require req_dept, and paint the header with the REQUISITION badge.
  const isVoucherReq = formData.document_type === 'voucher_req';

  // Remove debug logs
  // console.log('InvoiceEditForm render, formData:', formData);

  const handleSave = async () => {
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave(formData);
        setIsDirty(false);
      } catch (error) {
        console.error('Failed to save data:', error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    }
  };

  useEffect(() => {
    if (saveRef) saveRef.current = handleSave;
    return () => {
      if (saveRef) saveRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  const handleCancel = () => {
    setFormData({ ...data });
    setIsDirty(false);
    if (onCancel) {
      onCancel();
    }
  };

  const updateField = (path: string, value: any) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData as any;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      setIsDirty(true);
      if (onChange) onChange(newData);
      return newData;
    });
  };

  const addLineItem = (category: 'regular' | 'freight' | 'discount' = 'regular') => {
    // Per 2026-08-07 decision: freight and discount are their own line items.
    // Pre-fill the description so the clerk sees the intent immediately.
    const presetDescription =
      category === 'freight' ? 'Freight' : category === 'discount' ? 'Discount' : '';
    const newItem: LineItem = {
      description: presetDescription,
      quantity: category === 'regular' ? 0 : 1,
      unit_price: 0,
      total_price: 0,
      // Freight and discount lines don't require a PO match, so seed with an
      // empty GL. Regular lines still default to TBD (or blank when PO is set).
      gl_id: category !== 'regular' ? '' : hasPO ? '' : 'TBD',
      category,
      sub_disc: '',
      states_sales_tax_maintenance_code: '',
      sub_freight: '',
      tax_code: '',
    };

    setFormData(prev => {
      const newData = { ...prev, line_items: [...(prev.line_items || []), newItem] };
      setIsDirty(true);
      if (onChange) onChange(newData);
      return newData;
    });
  };

  const removeLineItem = (index: number) => {
    setFormData(prev => {
      const newData = { ...prev, line_items: prev.line_items?.filter((_, i) => i !== index) || [] };
      setIsDirty(true);
      if (onChange) onChange(newData);
      return newData;
    });
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    setFormData(prev => {
      const items = [...(prev.line_items || [])];
      if (items[index]) {
        items[index] = { ...items[index], [field]: value };
        // Auto-calculate total price
        if (field === 'quantity' || field === 'unit_price') {
          const quantity = field === 'quantity' ? value : items[index].quantity || 0;
          const unitPrice = field === 'unit_price' ? value : items[index].unit_price || 0;
          items[index].total_price = quantity * unitPrice;
        }
      }
      const newData = { ...prev, line_items: items };
      setIsDirty(true);
      if (onChange) onChange(newData);
      return newData;
    });
  };

// Move these components OUTSIDE to prevent recreation on each render

  return (
    // NOTE: no `overflow-hidden` on the root. It clips the sticky header's
    // connection to the scrolling ancestor two levels up (WorkspacePage's
    // .flex-1.overflow-y-auto), which silently disables `position: sticky`.
    // The header gets `rounded-t-lg` so its top corners still look right
    // without the outer clipping.
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg flex flex-col ${className}`}>
      {/* Header — sticky so Save Changes / Cancel stay in reach on long invoices.
          z-40 keeps it above line-item content (relative z-10-30). PO Line
          Matcher modal is z-[100] so it sits above this header when open. */}
      <div className="sticky top-0 z-40 bg-brand-600 dark:bg-brand-700 px-6 py-4 shadow-md rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-white mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-white">Edit Invoice Data</h2>
              {filename && (
                <p className="text-brand-100 text-sm mt-1">Source: {filename}</p>
              )}
            </div>
          </div>
          {isDirty && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center px-4 py-2 bg-green-700 hover:bg-green-800 text-white rounded-md text-sm font-semibold
                  disabled:opacity-50 transition-colors duration-200"
              >
                <CheckIcon className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex items-center px-4 py-2 bg-transparent border border-white/60 text-white rounded-md text-sm font-medium
                  hover:bg-white/10 disabled:opacity-50 transition-colors duration-200"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>


      {/* Form Content */}
      <div className="p-6 space-y-8 flex-1 overflow-y-auto relative z-10">
        {/* Voucher Requisition banner. Prominent, like Keystone's own
            "KeyNet Voucher Requisition Entry" screen, so the clerk sees
            immediately that this document is on the approval path and not
            a straight-to-register invoice. */}
        {isVoucherReq && (
          <div className="rounded-lg border-l-4 border-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-500 px-5 py-4">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-extrabold tracking-tight text-purple-900 dark:text-purple-100 uppercase">
                  Voucher Requisition
                </h2>
                <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
                  Routes through Keystone's approval workflow before payment.
                </p>
              </div>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wider bg-purple-200 dark:bg-purple-800/60 text-purple-900 dark:text-purple-100">
                APPROVAL REQUIRED
              </span>
            </div>
          </div>
        )}

        {/* Document Information */}
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Document Information
            </h3>
          </div>

          {/* Doc-type radio: Invoice vs Voucher Requisition. Only rendered
              when the user has both capabilities (role code 3). Users with
              only one capability (role 1 or 2) have the mode locked and the
              toggle hidden so they can't try to switch to a mode they
              don't have access to. */}
          {bothModes && (
            <FormRow label="Document Type">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="document_type"
                    className="h-4 w-4"
                    checked={formData.document_type === 'invoice' || !formData.document_type}
                    onChange={() => updateField('document_type', 'invoice')}
                  />
                  <span className="text-sm text-gray-900 dark:text-white">Invoice</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="document_type"
                    className="h-4 w-4"
                    checked={formData.document_type === 'voucher_req'}
                    onChange={() => updateField('document_type', 'voucher_req')}
                  />
                  <span className="text-sm text-gray-900 dark:text-white">Voucher Requisition</span>
                </label>
              </div>
            </FormRow>
          )}

          <FormRow label="Invoice Number" required>
            <TextInput
              value={formData.invoice_number}
              onChange={(value) => updateField('invoice_number', value)}
              placeholder="Enter invoice number"
              isOCRExtracted={isFieldExtracted(formData.invoice_number, originalData)}
            />
          </FormRow>

          {/* Requisition-only: department code (e.g. "101"). Post-SSO this
              should come from the KeyNet handshake payload. */}
          {isVoucherReq && (
            <FormRow label="Req Dept" required>
              <TextInput
                value={formData.req_dept || ''}
                onChange={(value) => updateField('req_dept', value)}
                placeholder="e.g. 101"
              />
            </FormRow>
          )}

          <FormRow label="Invoice Date" required>
            <TextInput
              value={formData.invoice_date}
              onChange={(value) => updateField('invoice_date', value)}
              type="date"
              isOCRExtracted={isFieldExtracted(formData.invoice_date, originalData)}
            />
          </FormRow>

          <FormRow label="Due Date">
            <TextInput
              value={formData.due_date}
              onChange={(value) => updateField('due_date', value)}
              type="date"
              isOCRExtracted={isFieldExtracted(formData.due_date, originalData)}
            />
          </FormRow>


        </div>

        {/* Vendor Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
            Vendor Information
          </h3>
          
          <FormRow label="Vendor Name" required>
            <VendorLookup
              vendorName={formData.vendor?.name || ''}
              onVendorSelect={(vendorDetails: any) => {
                // Update vendor information with Cleveland County database details
                updateField('vendor.name', vendorDetails.name);
                updateField('vendor.vendor_number', vendorDetails.vendor_number);
                updateField('vendor.vendor_address_id', vendorDetails.alt_vendor_key || '');
                updateField('vendor.type_1099', vendorDetails['1099_type'] || '');
                updateField('vendor.single_check', vendorDetails.single_check_flag || '');
                updateField('vendor.check_code', vendorDetails.check_code_default || '');

                // Update address fields
                const fullAddress = [
                  vendorDetails.address,
                  [vendorDetails.city, vendorDetails.state, vendorDetails.zip].filter(Boolean).join(', ')
                ].filter(Boolean).join(', ');
                updateField('vendor.address.full_address', fullAddress);

                // Pre-fill default GL from vendor record. When the vendor
                // has a default GL set, that's the default we should display.
                // PO/GL are no longer mutually exclusive, so we fill even
                // when a PO is set:
                // the PO's own GL, if any, is applied separately via the matcher
                // and can override.
                const vendorGl = (vendorDetails.gl_account_number || '').trim();
                const vendorGlDesc = (vendorDetails.gl_account_description || '').trim();
                if (vendorGl) {
                  let filledCount = 0;
                  setFormData((prev) => {
                    const items = (prev.line_items || []).map((item) => {
                      const current = (item.gl_id || '').trim();
                      if (current === '' || current === 'TBD') {
                        filledCount += 1;
                        return { ...item, gl_id: vendorGl };
                      }
                      return item;
                    });
                    return { ...prev, line_items: items };
                  });
                  if (filledCount > 0) {
                    const descSuffix = vendorGlDesc ? ` (${vendorGlDesc})` : '';
                    toast.success(
                      `Pre-filled GL ${vendorGl}${descSuffix} on ${filledCount} line item${filledCount === 1 ? '' : 's'} from vendor record`
                    );
                  }
                }
              }}
              onVendorChange={(vendorName) => {
                updateField('vendor.name', vendorName);
              }}
              className="w-full"
            />
          </FormRow>

          <FormRow label="Vendor Address ID Number">
            <VendorAddressSelect
              vendorNumber={formData.vendor?.vendor_number}
              userId={user?.id}
              currentAddressId={formData.vendor?.vendor_address_id}
              onChange={({ vendor_address_id, full_address, name }) => {
                updateField('vendor.vendor_address_id', vendor_address_id);
                // Always overwrite the address block, even when the new
                // address has no composed full_address. Otherwise picking
                // an address that resolves to null leaves the stale prior
                // address visible in the locked/read-only view.
                updateField('vendor.address.full_address', full_address || '');
                if (name) {
                  updateField('vendor.name', name);
                }
              }}
            />
          </FormRow>

          <FormRow label="Vendor Number">
            <TextInput
              value={formData.vendor?.vendor_number}
              onChange={(value) => updateField('vendor.vendor_number', value)}
              placeholder="Enter vendor number"
            />
          </FormRow>

          <FormRow label="Address">
            {/* Lock the address when it came from a matched vendor (vendor_number
                is only populated by VendorLookup, never by raw OCR). Clerks
                editing a vendor's address per-invoice is a vendor-file fix, not
                an invoice-row fix — same pattern as GL when a PO is selected.
                Exception: an "Edit" override lets clerks correct the address
                for cases where the vendor has multiple addresses and Keystone's
                default isn't the right one (Samantha's #6). */}
            {formData.vendor?.vendor_number && !addressManuallyUnlocked ? (
              <div className="flex items-start gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
                <LockClosedIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <span className="flex-1 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {formData.vendor?.address?.full_address || (
                    <span className="text-gray-400 italic">No address on vendor record</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setAddressManuallyUnlocked(true)}
                  className="text-xs font-medium text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 flex-shrink-0"
                  title="Edit the address for this invoice (e.g. when the vendor has a different remit-to address)"
                >
                  Edit
                </button>
              </div>
            ) : (
              <TextArea
                value={formData.vendor?.address?.full_address}
                onChange={(value) => updateField('vendor.address.full_address', value)}
                placeholder="Enter full address"
                rows={3}
                isOCRExtracted={isFieldExtracted(formData.vendor?.address?.full_address, originalData)}
              />
            )}
          </FormRow>

          <FormRow label="Post Date">
            <TextInput
              value={formData.vendor?.post_date}
              onChange={(value) => updateField('vendor.post_date', value)}
              type="date"
            />
          </FormRow>

          <FormRow label="1099 Type">
            {codes1099List && codes1099List.length > 0 ? (
              <select
                value={formData.vendor?.type_1099 || ''}
                onChange={(e) => updateField('vendor.type_1099', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="">(not subject to 1099)</option>
                {codes1099List.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}{c.desc ? ` - ${c.desc}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <TextInput
                value={formData.vendor?.type_1099}
                onChange={(value) => updateField('vendor.type_1099', value)}
                placeholder="Enter 1099 type"
              />
            )}
          </FormRow>

          <FormRow
            label="Single Check"
            tooltip="Cut a separate check for THIS invoice instead of combining with the vendor's other invoices in the next check run."
          >
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                checked={(formData.vendor?.single_check || '').toUpperCase() === 'Y'}
                onChange={(e) => updateField('vendor.single_check', e.target.checked ? 'Y' : 'N')}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Yes, cut a separate check for this invoice
              </span>
            </label>
          </FormRow>

          <FormRow
            label="Check Code"
            tooltip={
              vendorHasCheckCode
                ? "Pulled from the vendor's default on file in Keystone. Cannot be overridden here."
                : "Pick from the tenant's check codes. Falls back to a plain text field if the Keystone list is unreachable."
            }
          >
            {vendorHasCheckCode ? (
              <input
                type="text"
                readOnly
                value={formData.vendor?.check_code || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 dark:bg-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm cursor-not-allowed"
              />
            ) : checkCodesList && checkCodesList.length > 0 ? (
              <select
                value={formData.vendor?.check_code || ''}
                onChange={(e) => updateField('vendor.check_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="">(none)</option>
                {checkCodesList.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}{c.desc ? ` - ${c.desc}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <TextInput
                value={formData.vendor?.check_code}
                onChange={(value) => updateField('vendor.check_code', value)}
                placeholder="Enter check code"
              />
            )}
          </FormRow>

        </div>

        {/* PO Number — voucher requisitions have no PO on the source
            document by definition, so we hide the whole section. */}
        {!isVoucherReq && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
            PO Number
          </h3>
          
          {hasAnyGlId && !hasPO ? (
            <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3 flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">PO Number is locked</p>
                <p className="text-xs text-amber-600 dark:text-amber-300 mt-0.5">PO Number is locked because GL IDs are entered on line items. To use a PO Number instead, clear all GL IDs first.</p>
              </div>
            </div>
          ) : (
            <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700 rounded-lg p-3 flex items-start space-x-3">
              <InformationCircleIcon className="h-5 w-5 text-brand-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-brand-700 dark:text-brand-300">
                PO Number and GL ID are mutually exclusive. If a PO Number is entered, GL IDs on all line items will be locked. To enter GL IDs manually, leave the PO Number blank.
              </p>
            </div>
          )}
          <FormRow label="PO Number">
            <POLookup
              poNumber={formData.po_number || ''}
              vendorNumber={formData.vendor?.vendor_number || ''}
              onPOSelect={(poNumber, poDetails) => {
                updateField('po_number', poNumber);
                // Per 2026-08-25 meeting: PO and GL coexist. Do not clear
                // GL IDs when a PO number is entered.

                // Auto-fill invoice fields from PO details
                if (poDetails) {
                  // Invoice information
                  if (poDetails.invoice_number) {
                    updateField('invoice_number', poDetails.invoice_number);
                  }
                  if (poDetails.invoice_date) {
                    updateField('invoice_date', poDetails.invoice_date);
                  }
                  if (poDetails.due_date) {
                    updateField('due_date', poDetails.due_date);
                  }
                  if (poDetails.company_name) {
                    updateField('company_name', poDetails.company_name);
                  }
                  if (poDetails.document_type) {
                    updateField('document_type', poDetails.document_type);
                  }
                  if (poDetails.invoice_detail_line_items) {
                    updateField('invoice_detail_line_items', poDetails.invoice_detail_line_items);
                  }
                  
                  // Financial information
                  if (poDetails.subtotal !== undefined && poDetails.subtotal !== null) {
                    updateField('subtotal', poDetails.subtotal);
                  }
                  if (poDetails.total_tax !== undefined && poDetails.total_tax !== null) {
                    updateField('total_tax', poDetails.total_tax);
                  }
                  if (poDetails.total_amount !== undefined && poDetails.total_amount !== null) {
                    updateField('total_amount', poDetails.total_amount);
                  }
                  if (poDetails.currency) {
                    updateField('currency', poDetails.currency);
                  }
                  if (poDetails.discount !== undefined && poDetails.discount !== null) {
                    updateField('discount', poDetails.discount);
                  }
                  if (poDetails.freight !== undefined && poDetails.freight !== null) {
                    updateField('freight', poDetails.freight);
                  }
                  if (poDetails.notes) {
                    updateField('notes', poDetails.notes);
                  }
                  if (poDetails.terms) {
                    updateField('terms', poDetails.terms);
                  }
                  
                  // Vendor information
                  if (poDetails.vendor) {
                    const vendor = poDetails.vendor;
                    if (vendor.name) {
                      updateField('vendor.name', vendor.name);
                    }
                    if (vendor.vendor_number) {
                      updateField('vendor.vendor_number', vendor.vendor_number);
                    }
                    if (vendor.vendor_address_id) {
                      updateField('vendor.vendor_address_id', vendor.vendor_address_id);
                    }
                    if (vendor.type_1099) {
                      updateField('vendor.type_1099', vendor.type_1099);
                    }
                    if (vendor.single_check) {
                      updateField('vendor.single_check', vendor.single_check);
                    }
                    if (vendor.check_code) {
                      updateField('vendor.check_code', vendor.check_code);
                    }
                    if (vendor.post_date) {
                      updateField('vendor.post_date', vendor.post_date);
                    }
                    if (vendor.address) {
                      const address = vendor.address;
                      if (typeof address === 'string') {
                        updateField('vendor.address.full_address', address);
                      } else if (address.full_address) {
                        updateField('vendor.address.full_address', address.full_address);
                      }
                    }
                    if (vendor.phone) {
                      updateField('vendor.phone', vendor.phone);
                    }
                    if (vendor.email) {
                      updateField('vendor.email', vendor.email);
                    }
                  }
                  
                  // Customer information
                  if (poDetails.customer) {
                    const customer = poDetails.customer;
                    if (customer.name) {
                      updateField('customer.name', customer.name);
                    }
                    if (customer.customer_id) {
                      updateField('customer.customer_id', customer.customer_id);
                    }
                    if (customer.address) {
                      const address = customer.address;
                      if (typeof address === 'string') {
                        updateField('customer.address.full_address', address);
                      } else if (address.full_address) {
                        updateField('customer.address.full_address', address.full_address);
                      }
                    }
                    if (customer.phone) {
                      updateField('customer.phone', customer.phone);
                    }
                    if (customer.email) {
                      updateField('customer.email', customer.email);
                    }
                  }
                  
                  // Line items from the PO now include their own GL IDs
                  // (PO's default GL). Per 2026-08-25 meeting: keep them.
                  // Clerk can override on any line.
                  // Each PO line carries its unique detail_key; copy it into
                  // matched_po_detail_key so the write-back payload actually
                  // references the right PO detail record (prior to this,
                  // we were sending blank po_detail_key even after a PO
                  // Lookup pre-fill, which looked matched in the UI but was
                  // detached on export).
                  if (poDetails.line_items && Array.isArray(poDetails.line_items) && poDetails.line_items.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const prefilled = poDetails.line_items.map((li: any) => ({
                      ...li,
                      matched_po_detail_key:
                        li.matched_po_detail_key || li.detail_key || li.po_detail_key || undefined,
                    }));
                    updateField('line_items', prefilled);
                  }
                }
              }}
              onPOChange={(poNumber) => {
                const prev = (formData.po_number || '').trim();
                const next = (poNumber || '').trim();
                updateField('po_number', poNumber);
                // Per 2026-08-25 meeting: PO and GL coexist. Do not clear
                // existing line-item GL IDs when the PO field is edited.
                //
                // DO clear any per-line matched_po_detail_key when the PO
                // number actually changes, so stale detail keys from the
                // previous PO are not sent to Keystone (and don't make the
                // matcher's header read "6 of 6 matched" against a PO whose
                // lines don't include those keys).
                if (prev !== next) {
                  setFormData((p) => ({
                    ...p,
                    line_items: (p.line_items || []).map((li) => ({
                      ...li,
                      matched_po_detail_key: undefined,
                    })),
                  }));
                }
              }}
              className="w-full"
            />
          </FormRow>

        </div>
        )}

        {/* Financial Information. Per the 2026-08-07 decision, subtotal,
            discount, and freight are AUTO-COMPUTED from line items. Header
            fields are display-only so the clerk always sees a correct
            cross-reference against what they typed on the lines. Tax amount
            and Total remain editable for now. */}
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Financial Information
            </h3>
            <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Auto-computed from line items
            </span>
          </div>

          <FormRow label="Subtotal">
            <div className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300"
                 title="Sum of all regular line items (excludes freight and discount)">
              ${Number(formData.subtotal || 0).toFixed(2)}
            </div>
          </FormRow>

          <FormRow label="Tax Amount">
            <TextInput
              value={formData.total_tax?.toString()}
              onChange={(value) => updateField('total_tax', parseFloat(value) || 0)}
              type="number"
              placeholder="0.00"
              isOCRExtracted={isFieldExtracted(formData.total_tax, originalData)}
            />
          </FormRow>

          <FormRow
            label="Discount"
            tooltip="Auto-computed from line items tagged DISCOUNT. To add or edit a discount, add a line item with type 'Discount' below."
          >
            <div className="w-full px-3 py-2 text-sm bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-md text-gray-700 dark:text-gray-300"
                 title="Sum of all DISCOUNT-tagged line items">
              ${Number(formData.discount || 0).toFixed(2)}
            </div>
          </FormRow>

          <FormRow
            label="Freight"
            tooltip="Auto-computed from line items tagged FREIGHT. To add or edit freight, add a line item with type 'Freight' below."
          >
            <div className="w-full px-3 py-2 text-sm bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-md text-gray-700 dark:text-gray-300"
                 title="Sum of all FREIGHT-tagged line items">
              ${Number(formData.freight || 0).toFixed(2)}
            </div>
          </FormRow>

          <FormRow label="Total Amount" required>
            <div>
              <div className="flex items-center w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500">
                <span className="pl-3 pr-0.5 text-sm font-semibold text-gray-700 dark:text-gray-300 select-none">$</span>
                <input
                  type="number"
                  value={formData.total_amount?.toString() || ''}
                  onChange={(e) => updateField('total_amount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="flex-1 min-w-0 pl-0 pr-3 py-2 text-sm font-semibold bg-transparent border-0 focus:outline-none focus:ring-0 dark:text-white dark:placeholder-gray-400"
                  autoComplete="off"
                />
              </div>
              {(() => {
                // Three guards on Total Amount, in priority order:
                //   A) Fallback nudge: when OCR left total blank, offer a
                //      one-click fill from subtotal + adjustments. Common on
                //      invoices labeled "Balance" or "Balance Due".
                //   B) Line-sum mismatch: Keystone recomputes each line as
                //      (qty * unit price) and refuses the invoice unless the
                //      header total equals that sum exactly. We surface it
                //      here so the clerk fixes it before submit.
                //   C) Subtotal/freight/discount/tax composite mismatch:
                //      when subtotal + freight - discount
                //      + tax doesn't equal the header total within a cent.
                const currentTotal = Number(formData.total_amount || 0);
                const composite =
                  Number(formData.subtotal || 0) +
                  Number(formData.freight || 0) -
                  Number(formData.discount || 0) +
                  Number(formData.total_tax || 0);
                // Mirror Keystone's line-total math exactly:
                //   qty > 0  -> qty * unit_price
                //   qty <= 0 -> unit_price (flat charge for shipping/fees)
                const lineSum = (formData.line_items || []).reduce((acc, li: any) => {
                  const q = Number(li?.quantity || 0);
                  const up = Number(li?.unit_price || 0);
                  const v = q > 0 ? q * up : up;
                  return acc + (Number.isFinite(v) ? v : 0);
                }, 0);
                const fmt = (n: number) =>
                  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                // Case A: total blank, something to compute from
                if (currentTotal <= 0 && (composite > 0 || lineSum > 0)) {
                  const suggested = lineSum > 0 ? lineSum : composite;
                  return (
                    <button
                      type="button"
                      onClick={() => updateField('total_amount', Math.round(suggested * 100) / 100)}
                      className="mt-1 text-xs text-brand-600 dark:text-brand-400 hover:underline"
                    >
                      Use sum of line items (${fmt(suggested)})
                    </button>
                  );
                }

                // Case B: Keystone will reject this exact combination. Highest
                // priority warning because it blocks the Submit-to-Keystone path.
                if (currentTotal > 0 && lineSum > 0 && Math.abs(currentTotal - lineSum) >= 0.01) {
                  const diff = currentTotal - lineSum;
                  const diffLabel = diff > 0 ? `${fmt(Math.abs(diff))} higher` : `${fmt(Math.abs(diff))} lower`;
                  return (
                    <div className="mt-1 flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                      <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        Doesn't match sum of (qty &times; unit price) across line items (${fmt(lineSum)}, {diffLabel}).
                      </span>
                      <button
                        type="button"
                        onClick={() => updateField('total_amount', Math.round(lineSum * 100) / 100)}
                        className="underline hover:no-underline"
                      >
                        Use ${fmt(lineSum)}
                      </button>
                    </div>
                  );
                }

                // Case C: subtotal/freight/discount/tax composite mismatch.
                // Only shows when we DON'T have a reliable line-sum signal
                // (either no line items at all, or the line sum is zero).
                // When line sum is present and matches the total (Case B
                // passed), Keystone will accept the invoice regardless of
                // what subtotal/tax add up to — OCR often mis-captures those
                // fields and the composite warning becomes noise.
                const lineSumReliable = lineSum > 0;
                if (!lineSumReliable && currentTotal > 0 && composite > 0 && Math.abs(currentTotal - composite) >= 0.01) {
                  const diff = currentTotal - composite;
                  const diffLabel = diff > 0 ? `${fmt(Math.abs(diff))} higher` : `${fmt(Math.abs(diff))} lower`;
                  return (
                    <div className="mt-1 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                      <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        Doesn't match subtotal + freight - discount + tax (${fmt(composite)}, {diffLabel}).
                      </span>
                      <button
                        type="button"
                        onClick={() => updateField('total_amount', Math.round(composite * 100) / 100)}
                        className="underline hover:no-underline"
                      >
                        Use ${fmt(composite)}
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </FormRow>

          {isFeatureEnabled(user?.account, 'voucher_requisitions') && (
            <FormRow
              label="Needs Approval"
              tooltip={
                user?.requires_approval
                  ? 'Your account is set to Requires Approval, so every invoice you export is auto-flagged. Only an admin can change this.'
                  : 'When checked, the export marks this invoice as needing approval before Keystone pays it.'
              }
            >
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!user?.requires_approval || !!formData.needs_approval}
                  disabled={!!user?.requires_approval}
                  onChange={(e) => updateField('needs_approval', e.target.checked)}
                />
                {user?.requires_approval ? 'Locked on (user-level)' : 'Flag this invoice for approval'}
              </label>
            </FormRow>
          )}
        </div>

        {/* Line Items */}
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Line Items ({formData.line_items?.length || 0})
              </h3>
              {isVoucherReq ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                  <CheckIcon className="h-3 w-3" />
                  GL IDs: Manual (voucher)
                </span>
              ) : hasPO ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                  <LockClosedIcon className="h-3 w-3" />
                  GL IDs: Set by PO
                </span>
              ) : hasAnyGlId ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                  <CheckIcon className="h-3 w-3" />
                  GL IDs: Manual
                </span>
              ) : (formData.line_items?.length || 0) > 0 ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                  <ExclamationTriangleIcon className="h-3 w-3" />
                  GL IDs: Required
                </span>
              ) : null}
            </div>
            {/* Add Item picker: per 2026-08-07 decision, freight and discount
                are their own line-item types (not header fields), so the
                clerk needs an explicit way to add each type. */}
            <div className="relative inline-block">
              <button
                type="button"
                onClick={() => setAddItemMenuOpen((v) => !v)}
                className="flex items-center px-3 py-1 bg-brand-600 text-white rounded-md text-sm hover:bg-brand-700"
                aria-haspopup="menu"
                aria-expanded={addItemMenuOpen}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Item
                <span className="ml-1 text-xs opacity-80">▾</span>
              </button>
              {addItemMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setAddItemMenuOpen(false)}
                    aria-hidden="true"
                  />
                  <div
                    role="menu"
                    className="absolute right-0 mt-1 w-56 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg overflow-hidden"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { addLineItem('regular'); setAddItemMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
                      <span className="flex-1">Regular Item</span>
                      <span className="text-xs text-gray-400">product / service</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { addLineItem('freight'); setAddItemMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-2 border-t border-gray-100 dark:border-gray-700"
                    >
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                      <span className="flex-1">Freight</span>
                      <span className="text-xs text-gray-400">shipping / delivery</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { addLineItem('discount'); setAddItemMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 dark:hover:bg-amber-900/30 flex items-center gap-2 border-t border-gray-100 dark:border-gray-700"
                    >
                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                      <span className="flex-1">Discount</span>
                      <span className="text-xs text-gray-400">promo / credit</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {!isVoucherReq && hasPO && isFeatureEnabled(user?.account, 'po_matching') && (
            <div className="flex items-center gap-2 p-3 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg">
              <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                Match invoice line items to specific PO lines so the export
                charges the right PO detail record.
              </span>
              <button
                type="button"
                onClick={() => setShowPOMatcher(true)}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-md shadow-sm"
              >
                Match PO Lines
              </button>
            </div>
          )}
          {!isVoucherReq && showPOMatcher && hasPO && isFeatureEnabled(user?.account, 'po_matching') && (
            <POLineMatcher
              poNumber={formData.po_number || ''}
              vendorNumber={formData.vendor?.vendor_number || undefined}
              invoiceLines={formData.line_items || []}
              onMatch={(lineIdx, detailKey, poDefaults) => {
                setFormData((prev) => {
                  const items = [...(prev.line_items || [])];
                  if (!items[lineIdx]) return prev;
                  const cur = items[lineIdx];
                  const next = { ...cur, matched_po_detail_key: detailKey };
                  // Only backfill quantity/unit_price when OCR left them blank.
                  // Never overwrite a value already there. Partial-fulfillment
                  // cases (invoice qty 3 of PO qty 100) must stay put.
                  if (
                    (cur.quantity == null || cur.quantity === 0) &&
                    poDefaults?.quantity != null
                  ) {
                    next.quantity = poDefaults.quantity;
                  }
                  if (
                    (cur.unit_price == null || cur.unit_price === 0) &&
                    poDefaults?.unit_price != null
                  ) {
                    next.unit_price = poDefaults.unit_price;
                  }
                  const q = next.quantity || 0;
                  const p = next.unit_price || 0;
                  if (q && p && !cur.total_price) next.total_price = q * p;
                  // Auto-populate subject-to checkboxes from the PO line's
                  // flags. Only set when the clerk has not manually
                  // toggled: undefined means "PO knows better", explicit
                  // true/false means the clerk already decided.
                  if (
                    cur.subject_to_freight == null &&
                    poDefaults?.subject_to_freight != null
                  ) {
                    next.subject_to_freight = poDefaults.subject_to_freight;
                  }
                  if (
                    cur.subject_to_discount == null &&
                    poDefaults?.subject_to_discount != null
                  ) {
                    next.subject_to_discount = poDefaults.subject_to_discount;
                  }
                  // Fill the GL field visibly from the PO's own GL when the
                  // clerk has not already set one. Keystone imports on the
                  // detail_key regardless, but leaving the field blank made
                  // the match look like nothing happened. Cleveland's Arthur
                  // Services invoice was the concrete report.
                  const poGl = (poDefaults?.gl_account_number || '').trim();
                  if (poGl && !(cur.gl_id || '').trim()) {
                    next.gl_id = poGl;
                  }
                  items[lineIdx] = next;
                  const newData = { ...prev, line_items: items };
                  setIsDirty(true);
                  if (onChange) onChange(newData);
                  return newData;
                });
              }}
              onClose={() => setShowPOMatcher(false)}
            />
          )}

          {formData.line_items && formData.line_items.length > 0 ? (
            <div className="space-y-3">
              {formData.line_items.map((item, index) => {
                // Visual differentiation per 2026-08-07 decision: freight and
                // discount are their own line-item types. Distinct tint + badge
                // so the clerk can eyeball what's a real invoice line vs a
                // freight/discount adjustment.
                const category = (item.category || 'regular').toLowerCase();
                const isFreight = category === 'freight';
                const isDiscount = category === 'discount';
                const isRegular = !isFreight && !isDiscount;
                // Per 2026-08-25 meeting: when any regular line is subject_to_freight,
                // the standalone FREIGHT line item's amount is distributed across those
                // regular lines. The freight item stays visible so the clerk sees what
                // OCR extracted, but is grayed out with a DISTRIBUTED pill so it's
                // clear it no longer counts toward the subtotal.
                const freightDistributedNow = distributions.freight.isDistributed;
                const discountDistributedNow = distributions.discount.isDistributed;
                const isDistributedFreight = isFreight && freightDistributedNow;
                const isDistributedDiscount = isDiscount && discountDistributedNow;
                // When a freight/discount line is matched to a PO line,
                // we don't emit it as its own line to
                // Keystone. Its amount rolls up under the matched PO line. Show
                // it grayed with a "MERGED TO PO" pill so the clerk sees this.
                const isMergedIntoPO =
                  (isFreight || isDiscount) &&
                  !!(item.matched_po_detail_key || '').trim();
                const cardTint = isDistributedFreight || isDistributedDiscount || isMergedIntoPO
                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 opacity-60'
                  : isFreight
                    ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10'
                    : isDiscount
                      ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-gray-200 dark:border-gray-600';
                const badge = isFreight
                  ? { text: 'FREIGHT', bg: 'bg-blue-100 dark:bg-blue-900/40', fg: 'text-blue-800 dark:text-blue-200' }
                  : isDiscount
                    ? { text: 'DISCOUNT', bg: 'bg-amber-100 dark:bg-amber-900/40', fg: 'text-amber-800 dark:text-amber-200' }
                    : null;
                // Distributed shares for this specific line (if subject-to)
                const myFreightShare = distributions.freight.perLine[index];
                const myDiscountShare = distributions.discount.perLine[index];
                return (
                <div key={index} className={`p-4 border rounded-lg ${cardTint}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Item #{index + 1}
                      </span>
                      {badge && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider ${badge.bg} ${badge.fg}`}>
                          {badge.text}
                        </span>
                      )}
                      {(isDistributedFreight || isDistributedDiscount) && !isMergedIntoPO && (
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                          title="This amount is being distributed across line items that are 'Subject to Freight' or 'Subject to Discount'. It no longer counts in the subtotal."
                        >
                          DISTRIBUTED
                        </span>
                      )}
                      {isMergedIntoPO && (
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                          title="Matched to a PO line. This freight/discount will not export as its own line; its amount rolls up under the matched PO line."
                        >
                          MERGED TO PO
                        </span>
                      )}
                      {/* Visible match confirmation on regular lines. Without
                          this the clerk finished the matcher, saw an empty GL
                          field, and thought the match did not save (Cleveland
                          Arthur Services report). Freight/discount cases
                          already get MERGED TO PO above. */}
                      {isRegular && (item.matched_po_detail_key || '').trim() && (
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200"
                          title={`Matched to PO detail_key ${item.matched_po_detail_key}. Keystone attributes this line to that PO record on import.`}
                        >
                          MATCHED TO PO
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => removeLineItem(index)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Inline warning for freight/discount lines with no GL and no
                      PO match. Surfaces the same problem the export validator
                      catches (per Sarah feedback #36, the silent export failure)
                      but shows it before the clerk clicks Export. */}
                  {(isFreight || isDiscount) &&
                    !(item.gl_id || '').trim() &&
                    !(item.matched_po_detail_key || '').trim() && (
                      <div className="mb-3 flex items-start gap-2 px-3 py-2 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-800 rounded-md text-xs text-amber-900 dark:text-amber-100">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                          Assign a GL ID below{hasPO ? ' or open PO Line Matcher to match this to a PO freight/discount line' : ''}. Export will fail otherwise.
                        </span>
                      </div>
                    )}
                  {/* Per-line "Complete PO Line" toggle. Only appears when
                      (a) the line is matched to a PO detail record AND
                      (b) the user has the permission AND
                      (c) the user isn't in always-Y mode (in which case
                          every line is Y and per-line choice is moot).
                      Feeds column U (Comp PO Y/N) on export. */}
                  {item.matched_po_detail_key &&
                    user?.can_complete_po_line &&
                    !user?.default_complete_po_yes && (
                      <label
                        className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md text-xs text-amber-900 dark:text-amber-100 cursor-pointer"
                        title="When checked, Keystone will close this PO line on import and liquidate any remaining encumbered dollars. Leave unchecked to keep the PO open for future invoices against the same line."
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={item.complete_po_line === 'Y'}
                          onChange={(e) =>
                            updateLineItem(
                              index,
                              'complete_po_line',
                              e.target.checked ? 'Y' : 'N',
                            )
                          }
                        />
                        <span>
                          <strong>Close PO line on import</strong> — liquidate
                          any remaining encumbered dollars for PO detail{' '}
                          {item.matched_po_detail_key}
                        </span>
                      </label>
                    )}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                      <input
                        type="text"
                        value={item.description || ''}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        placeholder="Item description"
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
                          focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                          dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 relative z-30"
                        autoComplete="off"
                      />
                    </div>
                    {/* Voucher requisitions enter a flat Cost per line, not
                        Qty × Unit Price (per the requisition sample: cost,
                        gl_id, description are the only per-line inputs).
                        Write to total_price AND mirror to quantity=1 +
                        unit_price=cost so any downstream code that averages
                        or re-multiplies still gets consistent numbers. */}
                    {isVoucherReq ? (
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Cost</label>
                        <div className="flex items-center w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 relative z-30">
                          <span className="pl-3 pr-0.5 text-sm text-gray-700 dark:text-gray-300 select-none">$</span>
                          <input
                            type="number"
                            value={item.total_price?.toString() || ''}
                            onChange={(e) => {
                              const cost = parseFloat(e.target.value) || 0;
                              setFormData(prev => {
                                const items = [...(prev.line_items || [])];
                                if (items[index]) {
                                  items[index] = { ...items[index], total_price: cost, quantity: 1, unit_price: cost };
                                }
                                const next = { ...prev, line_items: items };
                                setIsDirty(true);
                                if (onChange) onChange(next);
                                return next;
                              });
                            }}
                            placeholder="0.00"
                            step="0.01"
                            className="flex-1 min-w-0 pl-0 pr-3 py-2 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 dark:text-white dark:placeholder-gray-400"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Quantity</label>
                          <input
                            type="number"
                            value={item.quantity?.toString() || ''}
                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            placeholder="0"
                            step="0.01"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
                              focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                              dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 relative z-30"
                            autoComplete="off"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Unit Price</label>
                          <input
                            type="number"
                            value={item.unit_price?.toString() || ''}
                            onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            step="0.01"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
                              focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                              dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 relative z-30"
                            autoComplete="off"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {/* GL ID sits on its own row so long account numbers (up to
                      45 chars: XXX-XXX-X-XXX-XX or RI 8-digit fund codes)
                      render in full without horizontal clipping. PO and
                      GL are no longer mutually exclusive: the clerk can
                      enter a GL
                      even on an invoice that has a PO number. The GL is
                      optional in that case (PO usually provides one) but
                      the clerk can override or supplement. */}
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      GL ID
                      {hasPO && (
                        <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">
                          (optional, PO usually provides one)
                        </span>
                      )}
                    </label>
                    <GLPicker
                      value={item.gl_id || ''}
                      onChange={(v) => updateLineItem(index, 'gl_id', v)}
                      onPick={(gl) => {
                        // When the clerk picks a GL from the dropdown,
                        // auto-fill the line description only if it's still
                        // empty. Never overwrite what the clerk typed.
                        if (!item.description?.trim() && gl.description?.trim()) {
                          updateLineItem(index, 'description', gl.description.trim());
                        }
                      }}
                      onCommit={(committed) => {
                        // When the clerk commits the FIRST line's GL (by
                        // picking or blurring), cascade it to any later
                        // line whose GL is still empty. Skip lines that
                        // already have a value: never overwrite the
                        // clerk's intent.
                        if (index !== 0 || !committed.trim()) return;
                        setFormData((prev) => ({
                          ...prev,
                          line_items: prev.line_items?.map((li, i) =>
                            i > 0 && !(li.gl_id || '').trim()
                              ? { ...li, gl_id: committed }
                              : li,
                          ),
                        }));
                      }}
                      vendorNumber={formData.vendor?.vendor_number || undefined}
                      vendorName={formData.vendor?.name || undefined}
                      glFormatExample={glFormatSettings.example}
                      glFormatRegex={glFormatSettings.regex}
                      glFormatPlaceholder={glFormatSettings.placeholder}
                      glFormatErrorLabel={glFormatSettings.label}
                    />
                  </div>

                  {/* Sub Disc / Sub Freight fields removed 2026-08-27 per user:
                      they are duplicated by the Subject to Freight / Subject to
                      Discount checkboxes below and no longer exported. Tax Code
                      stays inline for tenants with state tax enabled since
                      clerks touch it every taxable line. */}
                  {isStateTaxEnabled && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Tax Code
                        {Number(formData.total_tax || 0) > 0 && (
                          <span className="ml-1 text-red-500" title="Required when invoice has sales tax">*</span>
                        )}
                      </label>
                      {taxCodeList && taxCodeList.length > 0 ? (
                        <select
                          value={item.tax_code || ''}
                          onChange={(e) => updateLineItem(index, 'tax_code', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
                            focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                            dark:bg-gray-700 dark:text-white relative z-30"
                        >
                          <option value="">Select tax code...</option>
                          {taxCodeList.map((t) => (
                            <option key={t.code} value={t.code}>
                              {t.code}{t.description ? `, ${t.description}` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={item.tax_code || ''}
                          onChange={(e) => updateLineItem(index, 'tax_code', e.target.value)}
                          placeholder="e.g. NC"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md
                            focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                            dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 relative z-30"
                          autoComplete="off"
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Per 2026-08-25 meeting: on each REGULAR line, offer
                      "Subject to Freight" and "Subject to Discount" checkboxes.
                      When any regular line is subject-to, the total freight
                      (or discount) is distributed across those lines
                      proportional to their line_total (weighted, not
                      even). The PO API will eventually pre-populate
                      these based on the underlying requisition
                      flags; until then the clerk sets them manually. */}
                  {isRegular && (
                    <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-600 space-y-1.5">
                      <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5"
                          checked={Boolean(item.subject_to_freight)}
                          onChange={(e) => updateLineItem(index, 'subject_to_freight', e.target.checked)}
                          disabled={distributions.freight.totalFromLines <= 0}
                          title={distributions.freight.totalFromLines <= 0 ? "No freight on this invoice to distribute" : "Distribute the invoice's total freight proportionally to lines with this box checked"}
                        />
                        <span className="font-medium">Subject to Freight</span>
                        {distributions.freight.totalFromLines <= 0 && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                            (add a freight line to enable)
                          </span>
                        )}
                        {item.subject_to_freight && myFreightShare != null && (
                          <span className="ml-auto text-blue-700 dark:text-blue-300 font-semibold">
                            + ${myFreightShare.toFixed(2)}
                          </span>
                        )}
                      </label>
                      <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5"
                          checked={Boolean(item.subject_to_discount)}
                          onChange={(e) => updateLineItem(index, 'subject_to_discount', e.target.checked)}
                          disabled={distributions.discount.totalFromLines <= 0}
                          title={distributions.discount.totalFromLines <= 0 ? "No discount on this invoice to distribute" : "Distribute the invoice's total discount proportionally to lines with this box checked"}
                        />
                        <span className="font-medium">Subject to Discount</span>
                        {distributions.discount.totalFromLines <= 0 && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                            (add a discount line to enable)
                          </span>
                        )}
                        {item.subject_to_discount && myDiscountShare != null && (
                          <span className="ml-auto text-amber-700 dark:text-amber-300 font-semibold">
                            − ${myDiscountShare.toFixed(2)}
                          </span>
                        )}
                      </label>
                    </div>
                  )}

                  <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-600">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Line Total:
                      </span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        ${item.total_price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    {isRegular && (item.subject_to_freight || item.subject_to_discount) && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                        Effective (with distributions):{' '}
                        <span className="font-semibold text-gray-700 dark:text-gray-300">
                          ${(
                            Number(item.total_price || 0) +
                            (item.subject_to_freight && myFreightShare ? myFreightShare : 0) -
                            (item.subject_to_discount && myDiscountShare ? myDiscountShare : 0)
                          ).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>No line items added</p>
              <button
                onClick={() => addLineItem('regular')}
                className="mt-2 text-brand-600 hover:text-brand-800 dark:text-brand-400 text-sm"
              >
                Add the first line item
              </button>
            </div>
          )}
        </div>


      </div>
    </div>
  );
};

// Export without memo for now to debug
export default InvoiceEditForm;
